import {
  Box,
  Button,
  Flex,
  Icon,
  ModalContext,
  PaginationContext,
  PropertyHistoryModal,
  Text,
  UpgradeSubscriptionModalType,
  ViewerContext,
  useFromNow,
  useScreenResolution,
} from "dash-ui";
import { formatByMask, PROPERTY_PHONE_NUMBER_MASK } from "dash-common";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Wrapper } from "pages/properties/PropertyPreviewPage/components/PropertyPreviewSidebar/PropertyPreviewSidebar.styles";
import { useAddPropertyToTrack } from "common/hooks/useAddPropertyToTrack/useAddPropertyToTrack";
import {
  GetPropertyByIdQuery,
  useGetPropertyChangelogsQuery,
} from "gql/generated.graphql";
import moment from "moment";

const ContactLink = ({
  email,
  children,
}: {
  email?: string;
  children: ReactNode;
}) => {
  return email ? <a href={`mailto: ${email}`}>{children}</a> : <>{children}</>;
};

const SidebarInfoContainer = ({
  property,
  closeModal,
}: {
  property?: GetPropertyByIdQuery["getPropertyById"];
  closeModal?: () => void;
}) => {
  const { account, broker } = useContext(ViewerContext);
  const theme = useTheme();
  const { handleAddPropertyToTrack } = useAddPropertyToTrack();
  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);
  const { toggleModal, setUpgradeSubscriptionModal } = useContext(ModalContext);
  const { getRefetchProps, state, setTotal, setPerPage } =
    useContext(PaginationContext);
  const pagination = getRefetchProps(state);
  const { t } = useTranslation(["pages", "common"]);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { data, loading } = useGetPropertyChangelogsQuery({
    variables: {
      filter: { propertyId: property?._id },
      pagination,
      sort: [{ fieldName: "createdAt", sortOrder: -1 }],
    },
    fetchPolicy: "network-only",
    skip: !property?.hasChangelog || !isOpenHistoryModal,
  });
  const websiteUrl = property?.website;

  const { history, totalCount } = useMemo(
    () => ({
      history: data?.getPropertyChangelogs?.collection || [],
      totalCount: data?.getPropertyChangelogs?.totalCount || 0,
    }),
    [data]
  );

  const fromNow = useMemo(() => {
    const now = moment();
    const date = property?.lastUpdatedAtByChangelog;
    const diff = now.diff(date, "days");
    if (diff < 1) return "updated today";
    const string = `updated ${diff} days ago`;
    return string;
  }, [property]);

  useEffect(() => {
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  return (
    <Box>
      <PropertyHistoryModal
        isOpen={isOpenHistoryModal}
        onClose={() => setIsOpenHistoryModal(false)}
        loading={loading}
        data={history}
      />

      {isMobile && (
        <Button
          mt={3}
          w="100%"
          icon="bookmarkBorder"
          onClick={() => {
            if (broker) {
              handleAddPropertyToTrack(property || {}, account);
            } else {
              if (closeModal) closeModal();
              setUpgradeSubscriptionModal({
                type: UpgradeSubscriptionModalType.COMPLETE_ACCOUNT,
              });
              toggleModal(true, "UpgradeSubscriptionModal");
            }
          }}
        >
          {t("pages:trackingPage.trackProperty")}
        </Button>
      )}

      <Wrapper p={[0, 4, 4, 4]} my={[0, 3, 3, 3]} mt={[4, 3, 3, 3]}>
        <div className="mb-3">
          <Text color={theme.colors.n90} semibold>
            {t("propertyPage.contact")}
          </Text>
        </div>

        {property?.contactPhone?.number && (
          <Flex mb={3} alignItems="center">
            <Icon mr={2} name="mobile" size={22} color={theme.colors.g20} />
            <Text color={theme.colors.n80}>
              {property.contactPhone.dialCode || ""}{" "}
              {formatByMask(
                property.contactPhone?.number || "",
                PROPERTY_PHONE_NUMBER_MASK
              )}
            </Text>
          </Flex>
        )}

        {property?.contactEmail && (
          <div className="mb-3">
            <ContactLink email={property?.contactEmail || ""}>
              <Button
                colorVariation="success"
                w="100%"
                disabled={!property?.contactEmail}
                icon={"send"}
                variation={"outline"}
              >
                {t("common:labels.email")}
              </Button>
            </ContactLink>
          </div>
        )}

        <Button
          colorVariation="success"
          w="100%"
          disabled={!websiteUrl}
          icon={"world"}
          onClick={() => websiteUrl && window.open(websiteUrl)}
        >
          {t("common:labels.website")}
        </Button>
      </Wrapper>

      <Wrapper p={[0, 4, 4, 4]} mt={[6, 0, 0, 0]}>
        <Text color={theme.colors.n90} semibold>
          {t("propertyPage.lastUpdated")}
        </Text>

        <Flex mt={3} mb={4}>
          <Icon name="autorenew" color={theme.colors.g20} size={22} mr={3} />

          <Text color={theme.colors.n80}>
            {t("pages:propertyPreviewPage.historyLabel", {
              date: fromNow,
            })}
          </Text>
        </Flex>

        <Button
          colorVariation="success"
          w="100%"
          variation="outline"
          disabled={!property?.hasChangelog}
          onClick={() => setIsOpenHistoryModal(true)}
        >
          {t("propertyPage.history")}
        </Button>
      </Wrapper>
    </Box>
  );
};

export default SidebarInfoContainer;
